import * as d3 from 'd3'
import { motion } from 'motion/react'
import * as React from 'react'

import { cn, parseCalendarDate } from '@/utils'
import { CHART_COLORS } from './Constants'
import { ChartControls } from './Controls'
import { ChartGrid } from './Grid'
import { useChartBase } from './Hooks'
import { ChartTooltip } from './Tooltip'
import type { ChartDataPoint, ChartExportProps } from './types'
import {
  formatChartValue,
  granularityDateFormatter,
  inferDateGranularity,
  parseBucketDate,
} from './utils/formatters'

interface LineChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChartExportProps {
  data?: ChartDataPoint[]
  className?: string
  width?: string | number
  height?: number
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  color?: string
  strokeWidth?: number
  valueFormat?: 'currency' | 'number'
  showHoverControls?: boolean
}

const LineChart = React.forwardRef<HTMLDivElement, LineChartProps>(
  (
    {
      data = [],
      className,
      width = '100%',
      height = 400,
      marginTop = 20,
      marginRight = 30,
      marginBottom = 30,
      marginLeft = 40,
      color = CHART_COLORS[0],
      strokeWidth = 2,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      valueFormat,
      showHoverControls = false,
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      dimensions,
      isFullscreen,
      toggleFullscreen,
      fullscreenError: error,
      fullscreenSupported: isSupported,
      hoveredData,
      setHoveredData,
    } = useChartBase()

    const processedData = React.useMemo(() => {
      return data.map((d) => ({
        ...d,
        // Strict bucket parse first so labels like "Mar 26" don't get guessed
        // into a bogus year; fall back to the lenient parse for genuine
        // timestamps the bucket patterns don't cover.
        dateObj: parseBucketDate(d.date) ?? parseCalendarDate(d.date),
      }))
    }, [data])

    // Label axis ticks and the tooltip at the bucket granularity (year/month/
    // day) so a yearly chart reads "2020", not "Dec 2020". The agent can
    // override a point's label with its own `formattedDate`.
    const dateGranularity = React.useMemo(
      () =>
        inferDateGranularity(
          processedData
            .map((point) => point.dateObj)
            .filter((dateObj) => !Number.isNaN(dateObj.getTime())),
        ),
      [processedData],
    )
    const formatBucketDate = granularityDateFormatter(dateGranularity)

    const { xScale, yScale, linePath } = React.useMemo(() => {
      if (!dimensions.width || !dimensions.height || !processedData.length)
        return {}

      const x = d3
        .scaleUtc()
        .domain(d3.extent(processedData, (d) => d.dateObj) as [Date, Date])
        .range([marginLeft, dimensions.width - marginRight])

      // Include both the min and max so shortfall months (negative net
      // cash flow) plot below a visible zero baseline instead of
      // shooting off the bottom of the frame and drawing over the text
      // beneath it. Anchor zero inside the domain so the baseline stays
      // on-chart even when every value shares a sign.
      const numericValues = processedData
        .map((d) => Number(d.value))
        .filter((value) => !Number.isNaN(value))
      const dataMin = numericValues.length ? Math.min(...numericValues) : 0
      const dataMax = numericValues.length ? Math.max(...numericValues) : 0
      const y = d3
        .scaleLinear()
        .domain([Math.min(0, dataMin), Math.max(0, dataMax)])
        .nice()
        .range([dimensions.height - marginBottom, marginTop])

      const line = d3
        .line<ChartDataPoint>()
        .x((d) => x(d.dateObj!))
        .y((d) => {
          const value = Number(d.value)
          return y(Number.isNaN(value) ? 0 : value)
        })
        .curve(d3.curveMonotoneX)

      return { xScale: x, yScale: y, linePath: line(processedData) }
    }, [
      processedData,
      dimensions,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    ])

    const handlePointerMove = (event: React.PointerEvent) => {
      if (!xScale || !processedData.length) return
      const [xm] = d3.pointer(event)
      const i = d3
        .bisector((d: ChartDataPoint) => d.dateObj!)
        .center(processedData, xScale.invert(xm))
      setHoveredData(processedData[i])
    }

    const handlePointerLeave = () => {
      setHoveredData(null)
    }

    React.useImperativeHandle(ref, () => containerRef.current!, [])

    // When the caller declares a value unit (chat charts always do), format
    // the y-axis ticks and tooltip with it; otherwise keep the raw numbers.
    const formatValue = (value: number): string =>
      valueFormat ? formatChartValue(value, valueFormat) : String(value)
    const resolvedYTickFormatter = valueFormat
      ? (value: any) => formatValue(Number(value))
      : undefined

    if (!processedData.length) return null

    return (
      <div
        ref={containerRef}
        className={cn('w-full h-full relative group', className)}
        {...props}
      >
        {dimensions.width > 0 && dimensions.height > 0 && (
          <svg
            role="img"
            aria-label="Line chart"
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            className="overflow-visible"
          >
            <ChartGrid
              dimensions={dimensions}
              xScale={xScale}
              yScale={yScale}
              margin={{
                top: marginTop,
                right: marginRight,
                bottom: marginBottom,
                left: marginLeft,
              }}
              fontSize={isFullscreen ? 14 : 10}
              xTickFormatter={formatBucketDate}
              yTickFormatter={resolvedYTickFormatter}
            />

            <motion.path
              d={linePath || undefined}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            />

            <rect
              width={dimensions.width}
              height={dimensions.height}
              fill="transparent"
              onPointerMove={handlePointerMove}
              onPointerLeave={handlePointerLeave}
            />

            {hoveredData && xScale && yScale && (
              <g
                transform={`translate(${xScale(hoveredData.dateObj!)},${yScale(hoveredData.value)})`}
              >
                <circle r={4} fill={color} stroke="white" strokeWidth={2} />
              </g>
            )}
          </svg>
        )}

        {showHoverControls && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={error}
            fullscreenSupported={isSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={
            hoveredData && xScale && yScale
              ? {
                  x: xScale(hoveredData.dateObj!),
                  y: yScale(hoveredData.value),
                }
              : undefined
          }
        >
          {hoveredData && (
            <>
              <div className="font-semibold">
                {formatValue(Number(hoveredData.value))}
              </div>
              <div className="text-muted-foreground opacity-80">
                {hoveredData.formattedDate ??
                  (hoveredData.dateObj
                    ? formatBucketDate(hoveredData.dateObj)
                    : hoveredData.date)}
              </div>
            </>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
LineChart.displayName = 'LineChart'

export { LineChart }
